// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Shared compact score bar: a horizontal, wrapped list of player scores sorted
 * highest-first, with optional top-N limit, podium medals, and animated counters.
 *
 * Used by the per-player-score games (out_of_spec, faux_facts, imagined) via thin
 * preset wrappers. NOTE: cipher's team/agent + timer bar and the spectator podium
 * progress bar are deliberately different components, not consolidated here.
 */

import { AnimatedNumber } from '../../shared/AnimatedNumber';

export interface ScoreBarProps {
  /** player_id -> score. */
  scores: Record<string, number>;
  /** The viewer's player_id (highlighted). */
  playerId: string;
  /** Resolve a player_id to a display name. */
  getPlayerName: (pid: string) => string;
  /** Show only the top N scorers; omit to show all. */
  maxEntries?: number;
  /** Prefix the top three with 🥇/🥈/🥉. */
  showMedals?: boolean;
  /** Animate score changes via AnimatedNumber. */
  animated?: boolean;
  /** Compact (`sm`) vs default (`md`) spacing. */
  size?: 'sm' | 'md';
  /** When set, emits `${testIdPrefix}-score-bar` and `${testIdPrefix}-score-${pid}` test ids. */
  testIdPrefix?: string;
  /** Accessible label for the region. */
  ariaLabel?: string;
}

const SPACING = {
  sm: { outer: 'mb-4', inner: 'p-2', gap: 'gap-3' },
  md: { outer: 'mb-6', inner: 'p-3', gap: 'gap-4' },
} as const;

const MEDALS = ['🥇 ', '🥈 ', '🥉 '];

export function ScoreBar({
  scores,
  playerId,
  getPlayerName,
  maxEntries,
  showMedals = false,
  animated = false,
  size = 'md',
  testIdPrefix,
  ariaLabel = 'Current scores',
}: ScoreBarProps) {
  const space = SPACING[size];
  let entries = Object.entries(scores).sort(([, a], [, b]) => b - a);
  if (maxEntries !== undefined) {
    entries = entries.slice(0, maxEntries);
  }

  return (
    <div
      className={`max-w-4xl mx-auto ${space.outer}`}
      data-testid={testIdPrefix ? `${testIdPrefix}-score-bar` : undefined}
      role="region"
      aria-label={ariaLabel}
    >
      <div
        className={`rounded-lg ${space.inner} shadow-sm`}
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
        }}
      >
        <div
          className={`flex flex-wrap justify-center ${space.gap}`}
          role="list"
          aria-label="Player scores"
        >
          {entries.map(([pid, score], index) => (
            <div
              key={pid}
              className={`text-sm${animated ? ' flex items-center gap-1' : ''}`}
              style={{
                color: pid === playerId ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                fontWeight: pid === playerId ? 'bold' : 'normal',
              }}
              role="listitem"
              data-testid={testIdPrefix ? `${testIdPrefix}-score-${pid}` : undefined}
              aria-label={`${getPlayerName(pid)}: ${score} points`}
            >
              {showMedals && index < 3 ? MEDALS[index] : ''}
              {getPlayerName(pid)}: {animated ? <AnimatedNumber value={score} /> : score}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
